import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { StarRatingDisplay, StarRatingInput } from "@/components/StarRating";
import { GALLERY_SEED } from "@/lib/images";
import SectionOrnament from "@/components/SectionOrnament";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Upload, Star } from "lucide-react";

const FILTER_KEYS = ["all", "hochzeit", "verlobung", "weitere"];
const EVENT_KEYS = ["hochzeit", "verlobung", "weitere"];

// compress an image File to a jpeg data URL (max 1400px, quality 0.72)
function compressImage(file, maxDim = 1400, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
        else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Impressions() {
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [showForm, setShowForm] = useState(false);
  useSeo("Eindrücke & Bewertungen", "Impressions & avis", "Freigegebene Eindrücke und echte Bewertungen unserer Kundinnen und Kunden.", "Impressions validées et avis authentiques de nos clients.");

  const load = () => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const filterLabel = (k) => t(`impressions.filters.${k}`);

  // Gallery items: approved review photos (with category) + seed editorial images
  const reviewPhotos = reviews.flatMap((r) =>
    (r.photos || []).map((p, idx) => ({ url: p, category: r.event_type, review: r, key: `${r.id}-${idx}` }))
  );
  const seedPhotos = GALLERY_SEED.map((g, i) => ({ url: g.url, category: g.cat || "weitere", alt: lang === "fr" ? g.alt_fr : g.alt_de, key: `seed-${i}` }));
  const allPhotos = [...reviewPhotos, ...seedPhotos];
  const gallery = filter === "all" ? allPhotos : allPhotos.filter((p) => p.category === filter);

  return (
    <div data-testid="impressions-page">
      <PageHero overline={t("impressions.overline")} title={t("impressions.title")} subtitle={t("impressions.sub")} />

      {/* Rating summary */}
      {stats.count > 0 && (
        <section className="bg-secondary/40 py-10 px-6">
          <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
            <div className="flex items-center gap-3">
              <span className="font-heading text-4xl text-gold">{stats.average}</span>
              <StarRatingDisplay value={stats.average} size={20} />
            </div>
            <p className="text-sm text-ink-muted">{t("impressions.avgLabel").replace("{n}", stats.count)}</p>
          </div>
        </section>
      )}

      {/* Filters + Gallery */}
      <section className="relative bg-cream py-16 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {FILTER_KEYS.map((k) => (
              <button
                key={k}
                data-testid={`impressions-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`text-xs tracking-[0.2em] uppercase px-5 py-2 border transition-colors ${filter === k ? "bg-ink text-cream border-ink" : "border-border text-ink-muted hover:border-ink hover:text-ink"}`}
              >
                {filterLabel(k)}
              </button>
            ))}
          </div>

          {gallery.length === 0 ? (
            <p className="text-center text-ink-muted italic py-10" data-testid="gallery-empty">{t("impressions.emptyGallery")}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="impressions-gallery">
              {gallery.map((p, i) => (
                <Reveal key={p.key} delay={(i % 8) * 60} className={`zoom-parent cursor-pointer ${i % 6 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}>
                  <button type="button" onClick={() => setLightbox(p)} className="w-full h-full block" data-testid={`gallery-item-${i}`}>
                    <img src={p.url} alt={p.alt || p.review?.event_type || "Impression"} loading="lazy" className="w-full h-full object-cover zoom-img" />
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews list */}
      <section className="bg-secondary/40 py-20 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto">
          <p className="text-xs tracking-[0.35em] uppercase text-gold mb-10 text-center">{t("nav.reviews")}</p>
          {reviews.length === 0 ? (
            <p className="text-center text-ink-muted italic" data-testid="reviews-empty">{t("impressions.emptyReviews")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((r) => (
                <Reveal key={r.id} className="bg-card border border-border p-8">
                  <StarRatingDisplay value={r.rating} />
                  <p className="text-ink/80 font-light italic leading-relaxed my-5">« {r.text} »</p>
                  <p className="text-sm tracking-wider uppercase text-ink-muted">{r.name} · {filterLabel(r.event_type)}</p>
                  {r.instagram && <p className="text-xs text-gold mt-1">{r.instagram}</p>}
                </Reveal>
              ))}
            </div>
          )}

          <div className="text-center mt-14">
            <button onClick={() => setShowForm((s) => !s)} data-testid="impressions-share-toggle" className="inline-flex items-center gap-2 bg-ink text-cream px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500">
              <Star size={14} /> {t("impressions.shareBtn")}
            </button>
          </div>

          {showForm && <ReviewForm onDone={() => { setShowForm(false); load(); }} />}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl bg-cream border-border p-0 overflow-hidden">
          {lightbox && (
            <div>
              <img src={lightbox.url} alt="" className="w-full max-h-[70vh] object-contain bg-ink" />
              {lightbox.review && (
                <div className="p-6">
                  <StarRatingDisplay value={lightbox.review.rating} />
                  <p className="italic text-ink/80 my-3">« {lightbox.review.text} »</p>
                  <p className="text-sm text-ink-muted uppercase tracking-wider">{lightbox.review.name} · {filterLabel(lightbox.review.event_type)} {lightbox.review.event_date ? `· ${lightbox.review.event_date}` : ""}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewForm({ onDone }) {
  const { t } = useLanguage();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", event_type: "hochzeit", event_date: "", text: "", rating: 5, instagram: "", consent: false });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (files) => {
    const arr = Array.from(files).slice(0, 10 - photos.length);
    const valid = arr.filter((f) => ["image/jpeg", "image/png", "image/webp"].includes(f.type));
    if (valid.length < arr.length) toast.error("JPG, PNG, WebP");
    const compressed = await Promise.all(valid.map((f) => compressImage(f)));
    setPhotos((p) => [...p, ...compressed].slice(0, 10));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(t("reviewForm.consentRequired")); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, photos });
      toast.success(t("reviewForm.success"));
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="mt-12 max-w-2xl mx-auto bg-card border border-border p-8 sm:p-10 space-y-6">
      <h3 className="font-heading text-3xl font-light text-center">{t("reviewForm.title")}</h3>
      <div className="grid sm:grid-cols-2 gap-6">
        <Input label={t("reviewForm.name")} value={form.name} onChange={(e) => set("name", e.target.value)} testId="review-name" required />
        <Input label={t("reviewForm.email")} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} testId="review-email" required />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("reviewForm.eventType")}</label>
          <select data-testid="review-eventtype" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink">
            {EVENT_KEYS.map((k) => <option key={k} value={k}>{t(`impressions.filters.${k}`)}</option>)}
          </select>
        </div>
        <Input label={`${t("reviewForm.date")} (${t("common.optional")})`} type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} testId="review-date" />
      </div>
      <div>
        <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("reviewForm.rating")}</label>
        <StarRatingInput value={form.rating} onChange={(v) => set("rating", v)} testId="review-rating" />
      </div>
      <div>
        <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("reviewForm.text")}</label>
        <textarea data-testid="review-text" value={form.text} onChange={(e) => set("text", e.target.value)} required rows={4} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink resize-none" />
      </div>
      <Input label={`${t("reviewForm.instagram")} (${t("common.optional")})`} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} testId="review-instagram" />

      {/* Upload */}
      <div>
        <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("reviewForm.upload")}</label>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          data-testid="review-upload-zone"
          className="border border-dashed border-gold/50 p-8 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
        >
          <Upload size={22} className="mx-auto text-gold mb-2" />
          <p className="text-sm text-ink-muted">{t("reviewForm.uploadHint")}</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="review-file-input" onChange={(e) => handleFiles(e.target.files)} />
        </div>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-4" data-testid="review-photo-previews">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20">
                <img src={p} alt="" className="w-full h-full object-cover border border-border" />
                <button type="button" onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-ink text-cream rounded-full w-6 h-6 flex items-center justify-center" data-testid={`review-photo-remove-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="review-consent-label">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="review-consent" className="border-ink data-[state=checked]:bg-gold data-[state=checked]:border-gold mt-0.5" />
        <span className="text-sm text-ink-muted">{t("reviewForm.consent")}</span>
      </label>
      <p className="text-xs text-ink-muted italic">{t("reviewForm.privacyNote")}</p>

      <button type="submit" disabled={submitting} data-testid="review-submit" className="w-full bg-gold text-ink py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-ink hover:text-cream transition-colors disabled:opacity-60">
        {submitting ? t("common.sending") : t("reviewForm.submit")}
      </button>
    </form>
  );
}

function Input({ label, testId, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={onChange} required={required} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink transition-colors" />
    </div>
  );
}
